import { Check } from 'lucide-react';
import React, { useLayoutEffect, useRef, useState } from 'react';

type Step = {
  title: string;
  subtitle?: string;
};

type StepperProps = {
  steps: Step[];
  activeStep?: number; // 0-based
  className?: string;
};

export const Stepper: React.FC<StepperProps> = ({ steps, activeStep = 0, className }) => {
  const titleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [circleSizes, setCircleSizes] = useState<number[]>([]);

  const setTitleRef = React.useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      titleRefs.current[index] = el;
    },
    [],
  );

  useLayoutEffect(() => {
    if (!titleRefs.current.length) return;

    const sizes = titleRefs.current.map((ref) => ref?.offsetHeight || 64);

    setCircleSizes((prev) =>
      prev.length === sizes.length && prev.every((v, i) => v === sizes[i]) ? prev : sizes,
    );
  }, [steps.length]); // ✅ HERE

  return (
    <nav aria-label="Progress" className={`w-full flex items-center text-sm  ${className ?? ''}`}>
      {steps.map((step, index) => {
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;
        const circleSize = circleSizes[index] || 64;

        return (
          <React.Fragment key={index}>
            <div className="flex justify-start w-[50%] items-start gap-3 mr-4 bg-[#F2EBEE] p-3 rounded-lg">
              <div
                className={[
                  'flex-shrink-0 rounded-xl flex items-center justify-center border text-3xl font-bold text-[#2B1C25] transition',
                  isActive
                    ? 'bg-[#4A082D80]'
                    : isCompleted
                      ? 'bg-[#0BA96F]'
                      : 'bg-[#4A082D33] opacity-30',
                ].join(' ')}
                style={{ width: circleSize, height: circleSize }}
                aria-current={isActive ? 'step' : undefined}
              >
                {isCompleted ? <Check size={35} strokeWidth={1.5} /> : index + 1}
              </div>

              {/* Title + Subtitle */}
              <div ref={setTitleRef(index)} className="flex flex-col">
                <div className="flex flex-col">
                  {(() => {
                    const words = step.title.split(' ');
                    const length = Math.floor((words.length + 1) / 2);

                    if (words.length > 1) {
                      return (
                        <>
                          <span
                            className={[
                              'transition text-xl',
                              isActive
                                ? 'font-medium text-[#2B1C25]'
                                : isCompleted
                                  ? 'text-[#0BA96F]'
                                  : 'text-[#a2939c]',
                            ].join(' ')}
                          >
                            {words.slice(0, length).join(' ')}
                          </span>
                          <span
                            className={[
                              'transition text-xl',
                              isActive
                                ? 'font-medium text-[#2B1C25]'
                                : isCompleted
                                  ? 'text-[#0BA96F]'
                                  : 'text-[#a2939c]',
                            ].join(' ')}
                          >
                            {words.slice(length, words.length).join(' ')}
                          </span>
                        </>
                      );
                    }
                    return (
                      <span
                        className={[
                          'transition text-xl',
                          isActive
                            ? 'font-medium text-[#2B1C25]'
                            : isCompleted
                              ? 'text-[#0BA96F]'
                              : 'text-[#a2939c]',
                        ].join(' ')}
                      >
                        {step.title}
                      </span>
                    );
                  })()}
                </div>
                {step.subtitle && (
                  <span
                    className={[
                      'whitespace-nowrap transition font-bold text-xs',
                      isActive
                        ? 'text-[#2B1C25]'
                        : isCompleted
                          ? 'text-[#0BA96F]'
                          : 'text-[#a2939c]',
                    ].join(' ')}
                  >
                    {step.subtitle}
                  </span>
                )}
              </div>
            </div>

            {/* {index < steps.length - 1 && (
              <ChevronRight className="text-[#C5A9B8]" />
            )} */}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
